import { Link } from "@tanstack/react-router";
import { Instagram, Facebook, Phone, Mail } from "lucide-react";
import { Logo } from "@/components/Logo";
import { REVIEW_COUNT_TEXT } from "@/lib/booking";
import { internalPath } from "@/lib/paths";
import { openConsentSettings } from "@/lib/consent";

const salonLinks = [
  { label: "Barberare i Stockholm", to: "/barberare-stockholm" },
  { label: "Täby Centrum", to: "/barberare-stockholm-taby" },
  { label: "Åhléns City", to: "/barberare-stockholm-ahlens-city" },
  { label: "Åkersberga", to: "/barberare-stockholm-akersberga" },
  { label: "Arninge", to: "/barberare-stockholm-arninge" },
  { label: "Barbershop Stockholm", to: "/barbershop-stockholm" },
  { label: "Barberare Norrmalm", to: "/barberare-norrmalm" },
] as const;

const serviceLinks = [
  { label: "Herrklippning", to: "/herrklippning" },
  { label: "Skäggtrimning", to: "/skaggtrimning" },
  { label: "Klippning & Skägg", to: "/herrklippning-skaggtrimning" },
  { label: "Taper Fade", to: "/taper-fade" },
  { label: "Skin Fade", to: "/skin-fade" },
  { label: "Herrklippning Stockholm", to: "/herrklippning-stockholm" },
  { label: "Barnklippning", to: "/barnklippning-stockholm" },
  { label: "Seniorklippning", to: "/seniorklippning-pensionarsklippning" },
] as const;

const secondaryLinks = [
  { label: "Om oss", to: "/om-oss" },
  { label: "Presentkort", to: "/presentkort" },
  { label: "Inspiration & Guider", to: "/blogg" },
  { label: "Guider för herrhår och skägg", to: "/guider" },
  { label: "English", to: "/en" },
  { label: "Integritetspolicy", to: "/integritetspolicy" },
] as const;

function TikTokIcon({ size = 18 }: { size?: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V9.01a8.16 8.16 0 0 0 4.77 1.52V7.09a4.85 4.85 0 0 1-1.84-.4Z" />
    </svg>
  );
}

export function Footer() {
  return (
    <footer className="mt-24 border-t border-border bg-secondary/40">
      <div className="container-page py-14 grid gap-10 md:grid-cols-4">
        <div>
          <Logo size={64} asLink={false} />
          <p className="mt-4 text-sm text-muted-foreground max-w-xs">
            Stockholms ledande barberkedja. Fyra salonger, {REVIEW_COUNT_TEXT}, professionella
            barberare.
          </p>
          <div className="mt-4 space-y-1.5 text-sm">
            <a
              href="tel:+46837 1156"
              className="flex items-center gap-2 text-foreground/80 hover:text-brass-deep"
            >
              <Phone size={14} className="text-brass shrink-0" aria-hidden />
              08-37 11 56
            </a>
            <a
              href="mailto:info@tailorsbarber.com"
              className="flex items-center gap-2 text-foreground/80 hover:text-brass-deep"
            >
              <Mail size={14} className="text-brass shrink-0" aria-hidden />
              info@tailorsbarber.com
            </a>
          </div>
          <div className="mt-5 flex items-center gap-2">
            <a
              href="https://www.instagram.com/tailorsbarber"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Tailor's Barber på Instagram"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border text-foreground/70 hover:text-brass-deep hover:border-brass transition-colors"
            >
              <Instagram size={18} aria-hidden />
            </a>
            <a
              href="https://www.facebook.com/tailorsbarber/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Tailor's Barber på Facebook"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border text-foreground/70 hover:text-brass-deep hover:border-brass transition-colors"
            >
              <Facebook size={18} aria-hidden />
            </a>
            <a
              href="https://www.tiktok.com/@tailorsbarber.se"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Tailor's Barber på TikTok"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border text-foreground/70 hover:text-brass-deep hover:border-brass transition-colors"
            >
              <TikTokIcon size={16} />
            </a>
          </div>
        </div>
        <div>
          <h3 className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-3">
            Salonger
          </h3>
          <ul className="space-y-2 text-sm">
            {salonLinks.map((n) => (
              <li key={n.to}>
                <Link to={internalPath(n.to)} className="hover:text-brass-deep">
                  {n.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h3 className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-3">
            Tjänster
          </h3>
          <ul className="space-y-2 text-sm">
            {serviceLinks.map((n) => (
              <li key={n.to}>
                <Link to={internalPath(n.to)} className="hover:text-brass-deep">
                  {n.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h3 className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-3">Övrigt</h3>
          <ul className="space-y-2 text-sm">
            {secondaryLinks.map((n) => (
              <li key={n.to}>
                <Link to={internalPath(n.to)} className="hover:text-brass-deep">
                  {n.label}
                </Link>
              </li>
            ))}
            <li>
              <button
                type="button"
                onClick={() => openConsentSettings()}
                className="hover:text-brass-deep"
              >
                Cookieinställningar
              </button>
            </li>
            <li>
              <a href="/#drop-in" className="hover:text-brass-deep">
                Drop-in
              </a>
            </li>
          </ul>
        </div>
      </div>
      <div className="container-page pb-10 text-xs text-muted-foreground">
        © {new Date().getFullYear()} Tailor&apos;s Barber. Alla rättigheter förbehållna.
      </div>
    </footer>
  );
}
